import {
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Typography,
  alpha,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  LocationOnOutlined,
  ContentCut,
  PersonOutlineRounded,
  StickyNote2Outlined,
  LoginRounded,
  LogoutRounded,
  ScheduleRounded,
  BlockRounded,
  EventBusyRounded,
  EditOutlined,
  TaskAltRounded,
  PaymentsOutlined,
  MarkEmailReadOutlined,
  NotificationsActiveOutlined,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { clampStep15, formatDurationHuman, formatGapHuman } from "./durationFormat";
import { isLastBlockOfMultiStepService } from "./segmentDurationOverrides";
import { buildSegmentTimeline } from "./segmentGapOverrides";
import { formatFloatingWallClock } from "./config";
import { getPaymentMethodLabel } from "../../utils/paymentMethods";
import { formatMoney, sumDeposits } from "../../utils/appointmentDeposits";

export const ScheduleEntryStatus = {
  Scheduled: 0,
  InProgress: 1,
  Finished: 2,
  Canceled: 3,
  NoShow: 4,
};

export const ScheduleEntryStatusMap = {
  0: "Scheduled",
  1: "In Progress",
  2: "Finished",
  3: "Canceled",
  4: "No Show",
};

/** Labels for the deprecated integer `cancellation_reason`; only used for rows not yet backfilled. */
export const LEGACY_CANCELLATION_REASON_LABELS = {
  0: "Other",
  1: "Client Requested",
  2: "Double Booking",
  3: "Emergency",
  4: "Stylist Unavailable",
};

const InfoRow = ({ icon, label, children }) => (
  <Stack direction="row" alignItems="flex-start" gap={1}>
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: 1.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: (t) => alpha(t.palette.text.primary, 0.05),
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0, flex: 1, pt: 0.25 }}>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          lineHeight: 1.2,
          color: (theme) =>
            theme.palette.mode === "dark" ? "rgba(255,255,255,0.62)" : "#00000090",
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  </Stack>
);

const StatusBanner = ({ color, bgcolor, children }) => (
  <Box
    sx={{
      px: 2,
      py: 0.75,
      borderRadius: 1.5,
      bgcolor,
      color,
    }}
  >
    {children}
  </Box>
);

const StatusSeal = ({ text, color }) => (
  <Box
    sx={{
      position: "absolute",
      top: 24,
      right: 20,
      transform: "rotate(-14deg)",
      px: 1.25,
      py: 0.75,
      border: `6px double ${color}`,
      pointerEvents: "none",
      userSelect: "none",
    }}
  >
    <Typography
      variant="caption"
      sx={{
        fontWeight: 600,
        fontSize: 24,
        textTransform: "uppercase",
        lineHeight: 1,
        letterSpacing: "-0.05em",
        color,
        whiteSpace: "nowrap",
      }}
    >
      {text}
    </Typography>
  </Box>
);

export const renderDetailedItem = (
  fields,
  event,
  events,
  onStartAppointment,
  onFinishAppointment,
  onMarkAsLate,
  onCancelAppointment,
  onNoShow,
  serviceDurationEdit = null,
  paymentOptions = null
) => {
  return (
    <>
      {fields.map((_, index) => {
        if (index > 0) return null;

        const services = event?.uniqueServices || [];
        const bookedByDisplay =
          event?.booked_by_name && String(event.booked_by_name).trim()
            ? String(event.booked_by_name).trim()
            : "-";
        const isScheduled =
          event?.appointment_status == ScheduleEntryStatus.Scheduled;
        const isInProgress =
          event?.appointment_status == ScheduleEntryStatus.InProgress;
        const isCanceled =
          event?.appointment_status == ScheduleEntryStatus.Canceled;
        const isNoShow =
          event?.appointment_status == ScheduleEntryStatus.NoShow;
        const isFinished =
          event?.appointment_status == ScheduleEntryStatus.Finished;
        const showLate = isScheduled && !event?.is_late;
        const hasActions = isScheduled || isInProgress;

        const seal =
          (isNoShow && { text: "NO SHOW", color: "#d32f2f" }) ||
          (isCanceled && { text: "CANCELED", color: "#616161" }) ||
          null;

        return (
          <Stack key={index} spacing={0}>
            {/* Details grid */}
            <Stack
              direction="row"
              sx={{ px: 2, py: 1.5, position: "relative" }}
              gap={2}
            >
              <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                <InfoRow
                  icon={<PersonOutlineRounded sx={{ fontSize: 16 }} />}
                  label="Stylist"
                >
                  <Typography variant="body2" sx={{ fontWeight: 650 }} noWrap>
                    {event?.user_name || "\u2014"}
                  </Typography>
                </InfoRow>

                <InfoRow
                  icon={<LocationOnOutlined sx={{ fontSize: 16 }} />}
                  label="Location"
                >
                  <Typography variant="body2" noWrap>
                    {event?.location?.location_name || "\u2014"}
                  </Typography>
                </InfoRow>

                <InfoRow
                  icon={<StickyNote2Outlined sx={{ fontSize: 16 }} />}
                  label="Booked by"
                >
                  <Typography variant="body2" noWrap>
                    {bookedByDisplay}
                  </Typography>
                </InfoRow>

                {event?.isClientConfirmed && (
                  <InfoRow
                    icon={<TaskAltRounded sx={{ fontSize: 16 }} />}
                    label="Client confirmed"
                  >
                    <Typography variant="body2" noWrap>
                      {event?.client_confirmed_at
                        ? dayjs(event.client_confirmed_at).format(
                            "MMM D, h:mm A"
                          )
                        : "Yes"}
                    </Typography>
                  </InfoRow>
                )}

                {event?.confirmation_email_sent_at && (
                  <InfoRow
                    icon={<MarkEmailReadOutlined sx={{ fontSize: 16 }} />}
                    label="Confirmation email"
                  >
                    <Typography variant="body2" noWrap>
                      Sent{" "}
                      {dayjs(event.confirmation_email_sent_at).format(
                        "MMM D, h:mm A"
                      )}
                    </Typography>
                  </InfoRow>
                )}

                {event?.reminder_email_sent_at && (
                  <InfoRow
                    icon={<NotificationsActiveOutlined sx={{ fontSize: 16 }} />}
                    label="Reminder email"
                  >
                    <Typography variant="body2" noWrap>
                      Sent{" "}
                      {dayjs(event.reminder_email_sent_at).format(
                        "MMM D, h:mm A"
                      )}
                    </Typography>
                  </InfoRow>
                )}

                {serviceDurationEdit?.segments?.length > 0 ? (
                  <InfoRow
                    icon={<ContentCut sx={{ fontSize: 15 }} />}
                    label="Services"
                  >
                    <Stack spacing={1} mt={0.25} sx={{ width: "100%" }}>
                      {buildSegmentTimeline(serviceDurationEdit.segments).map(
                        (item) => {
                          if (item.type === "gap") {
                            return (
                              <Stack
                                key={item.key}
                                direction="row"
                                alignItems="center"
                                gap={0.75}
                                sx={{
                                  width: "100%",
                                  minWidth: 0,
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 2,
                                  border: "1px dashed",
                                  borderColor: (theme) =>
                                    theme.palette.mode === "dark"
                                      ? "rgba(255,255,255,0.28)"
                                      : "#00000040",
                                  backgroundColor: "rgba(197, 160, 111, 0.08)",
                                }}
                              >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, lineHeight: 1.25 }}
                                    noWrap
                                  >
                                    Processing gap
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", lineHeight: 1.35 }}
                                  >
                                    {formatFloatingWallClock(item.gapStart, true)}{" "}
                                    – {formatFloatingWallClock(item.gapEnd, true)} ·{" "}
                                    {formatGapHuman(item.gapMinutes)}
                                  </Typography>
                                </Box>
                                {serviceDurationEdit.canEdit &&
                                  item.serviceId != null &&
                                  typeof serviceDurationEdit.onEditGap ===
                                    "function" && (
                                    <Tooltip title="Edit gap">
                                      <IconButton
                                        size="small"
                                        aria-label={`Edit gap for ${item.title}`}
                                        sx={{ flexShrink: 0 }}
                                        onClick={() =>
                                          serviceDurationEdit.onEditGap(item)
                                        }
                                      >
                                        <EditOutlined sx={{ fontSize: 18 }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                              </Stack>
                            );
                          }

                          const seg = item.segment;
                          const svc = seg.services?.[0];
                          const title = svc?.title ?? seg.title ?? "Service";
                          const startVal = svc?.serviceStart ?? seg.start;
                          const endVal = svc?.serviceEnd ?? seg.end;
                          const allowZero = isLastBlockOfMultiStepService(
                            serviceDurationEdit.segments,
                            seg
                          );
                          const dm = clampStep15(
                            dayjs(endVal).diff(dayjs(startVal), "minute"),
                            allowZero ? 0 : 15
                          );
                          return (
                            <Stack
                              key={item.key}
                              direction="row"
                              alignItems="center"
                              gap={0.75}
                              sx={{
                                width: "100%",
                                minWidth: 0,
                                px: 1,
                                py: 0.5,
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "rgba(255,255,255,0.16)"
                                    : "#00000030",
                                backgroundColor: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "rgba(255,255,255,0.04)"
                                    : "#00000005",
                              }}
                            >
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 650, lineHeight: 1.25 }}
                                  noWrap
                                >
                                  {title}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: "block", lineHeight: 1.35 }}
                                >
                                  {formatFloatingWallClock(startVal, true)} –{" "}
                                  {formatFloatingWallClock(endVal, true)} ·{" "}
                                  {formatDurationHuman(dm)}
                                </Typography>
                              </Box>
                              {serviceDurationEdit.canEdit && (
                                <Tooltip title="Edit duration">
                                  <IconButton
                                    size="small"
                                    aria-label={`Edit duration for ${title}`}
                                    sx={{ flexShrink: 0 }}
                                    onClick={() =>
                                      serviceDurationEdit.onEditSegment(seg)
                                    }
                                  >
                                    <EditOutlined sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          );
                        }
                      )}
                    </Stack>
                  </InfoRow>
                ) : (
                  services.length > 0 && (
                    <InfoRow
                      icon={<ContentCut sx={{ fontSize: 15 }} />}
                      label="Services"
                    >
                      <Stack
                        direction="row"
                        gap={0.5}
                        flexWrap="wrap"
                        mt={0.25}
                      >
                        {services.map((s, i) => (
                          <Chip
                            key={`${s.service_id}${i}`}
                            label={s.title}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: "0.72rem",
                              fontWeight: 600,
                              bgcolor: (t) =>
                                alpha(t.palette.primary.main, 0.08),
                              color: "primary.main",
                            }}
                          />
                        ))}
                      </Stack>
                    </InfoRow>
                  )
                )}

                {event?.deposits?.length > 0 && (
                  <InfoRow
                    icon={<PaymentsOutlined sx={{ fontSize: 16 }} />}
                    label="Deposit on file"
                  >
                    <Stack spacing={0.25}>
                      {event.deposits.map((deposit) => (
                        <Typography
                          key={deposit.id}
                          variant="body2"
                          sx={{ fontWeight: 650 }}
                          noWrap
                        >
                          {deposit.label} · {formatMoney(deposit.amount)}
                        </Typography>
                      ))}
                      {event.deposits.length > 1 && (
                        <Typography variant="caption" color="text.secondary">
                          Total {formatMoney(sumDeposits(event.deposits))}
                        </Typography>
                      )}
                    </Stack>
                  </InfoRow>
                )}

                {isFinished &&
                  paymentOptions?.canView &&
                  event?.hasReceipt && (
                    <InfoRow
                      icon={<PaymentsOutlined sx={{ fontSize: 16 }} />}
                      label="Payment"
                    >
                      <Typography variant="body2" sx={{ fontWeight: 650 }} noWrap>
                        {getPaymentMethodLabel(event?.paymentMethod)}
                      </Typography>
                    </InfoRow>
                  )}
              </Stack>

              {seal && <StatusSeal text={seal.text} color={seal.color} />}

              {isCanceled && (
                <Box
                  sx={{
                    position: "absolute",
                    right: 12,
                    bottom: 40,
                    minWidth: 80,
                    maxWidth: 110,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 0.25,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      lineHeight: 1.25,
                      color: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.62)"
                          : "#00000090",
                    }}
                  >
                    Reason
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, lineHeight: 1.25 }}
                  >
                    {event.cancellation_reason_name ??
                      LEGACY_CANCELLATION_REASON_LABELS[event.cancellation_reason] ??
                      "\u2014"}
                  </Typography>
                </Box>
              )}
            </Stack>

            {/* Action footer */}
            {hasActions && (
              <>
                <Divider />
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{
                    px: 2,
                    py: 1,
                    bgcolor: (t) => alpha(t.palette.text.primary, 0.02),
                  }}
                  gap={0.75}
                >
                  {/* Primary CTA */}
                  {isScheduled && (
                    <Button
                      variant="contained"
                      size="small"
                      disableElevation
                      startIcon={<LoginRounded sx={{ fontSize: 15 }} />}
                      sx={{
                        textTransform: "none",
                        borderRadius: "6px",
                        px: 1.25,
                        py: 0.75,
                        fontSize: 14,
                        lineHeight: 1,
                        fontWeight: 700,
                        bgcolor: "#C5A06F",
                        color: "#000",
                        "&:hover": { bgcolor: "#b8915f" },
                      }}
                      onClick={() => onStartAppointment(event)}
                    >
                      Check-in
                    </Button>
                  )}
                  {isInProgress && (
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      disableElevation
                      startIcon={<LogoutRounded sx={{ fontSize: 15 }} />}
                      sx={{
                        textTransform: "none",
                        borderRadius: "6px",
                        px: 1.25,
                        py: 0.75,
                        fontSize: 14,
                        lineHeight: 1,
                        fontWeight: 700,
                      }}
                      onClick={() => onFinishAppointment(event.appointmentId)}
                    >
                      Checkout
                    </Button>
                  )}

                  {/* Spacer */}
                  <Box sx={{ flex: 1 }} />

                  {/* Secondary actions */}
                  {showLate && (
                    <IconActionButton
                      icon={<ScheduleRounded sx={{ fontSize: 15 }} />}
                      label="Late"
                      color="#ed6c02"
                      onClick={() => onMarkAsLate(event.appointmentId)}
                    />
                  )}
                  {isScheduled && (
                    <>
                      <IconActionButton
                        icon={<BlockRounded sx={{ fontSize: 15 }} />}
                        label="No show"
                        color="#d32f2f"
                        onClick={() => onNoShow(event.appointmentId)}
                      />
                      <IconActionButton
                        icon={<EventBusyRounded sx={{ fontSize: 15 }} />}
                        label="Cancel"
                        color="#d32f2f"
                        onClick={() => onCancelAppointment(event.appointmentId)}
                      />
                    </>
                  )}
                </Stack>
              </>
            )}
          </Stack>
        );
      })}
    </>
  );
};

const IconActionButton = ({ icon, label, color, onClick }) => (
  <Button
    size="small"
    onClick={onClick}
    sx={{
      minWidth: 0,
      textTransform: "none",
      fontWeight: 600,
      fontSize: 12,
      lineHeight: 1,
      color,
      borderRadius: 1.5,
      whiteSpace: "nowrap",
      px: 1,
      py: 1,
      gap: 0.5,
      bgcolor: (t) => alpha(color, 0.06),
      "&:hover": {
        bgcolor: (t) => alpha(color, 0.14),
      },
    }}
  >
    {icon}
    {label}
  </Button>
);
